/**
 * Ato Desktop bridge — the PWA's side of the `ato://` intent boundary.
 *
 * When the PWA is embedded in the Ato Desktop shell (the Home WebView), the
 * desktop injects `window.__ATO_DESKTOP__` and tags its User-Agent. In that
 * context the PWA can *emit* intents — it can never execute locally. It
 * navigates to an `ato://…` URL; the desktop's navigation handler intercepts,
 * validates the origin + verb + payload (see `crates/desktop/src/intent.rs`),
 * and then applies the desktop-side per-verb policy: `runner/register` =
 * browser device-flow, `runner/start` / `runner/stop` = origin-gated +
 * activity-logged, `run` = acknowledged only until native run execution lands.
 *
 * Outside the desktop (a normal browser tab) `isDesktop()` is false and these
 * helpers must not be used — callers fall back to the CLI instructions.
 *
 * The intent URIs built here MUST match the desktop classifier exactly.
 */

/** Ack for a launch handed to the desktop over the injected IPC bridge. */
export interface DesktopLaunchAck {
  accepted: boolean;
  reason?: string;
}

/** Payload for the launch handoff IPC. launch_id ONLY (plus the capsule ref
 *  for display) — NEVER an app_url / runner URL / IP / token. The desktop
 *  re-fetches the launch owner-authenticated; the id alone opens nothing. */
export interface DesktopLaunchPayload {
  launch_id: string;
  capsule_ref?: string;
}

export interface DesktopInfo {
  version: string;
  platform: string;
  /**
   * Launch handoff IPC (launch-unification plan §4), injected by newer
   * desktops via wry's ipc_handler. Returns an ack: `accepted: true` means
   * the desktop now owns the launch's visibility (the PWA stops polling);
   * anything else means the PWA keeps polling in the browser. Older desktops
   * don't inject it — callers must treat its absence as not-accepted.
   */
  launch?: (
    payload: DesktopLaunchPayload,
  ) => Promise<DesktopLaunchAck> | DesktopLaunchAck;
}

declare global {
  interface Window {
    /** Injected by Ato Desktop into the embedded Home WebView. */
    __ATO_DESKTOP__?: DesktopInfo;
  }
}

/** The Desktop descriptor when running embedded in Ato Desktop, else `null`. */
export function desktopInfo(): DesktopInfo | null {
  if (typeof window === "undefined") return null;
  const info = window.__ATO_DESKTOP__;
  if (info && typeof info.version === "string" && typeof info.platform === "string") {
    return info;
  }
  return null;
}

/** Whether the PWA is running embedded inside the Ato Desktop shell. */
export function isDesktop(): boolean {
  return desktopInfo() !== null;
}

// --- Intent URIs (pure builders; must match crates/desktop/src/intent.rs) ---

export const RUNNER_REGISTER_URI = "ato://runner/register";
export const RUNNER_START_URI = "ato://runner/start";
export const RUNNER_STOP_URI = "ato://runner/stop";

/** Build an `ato://run?source=…[&run_id=…]` intent URI (params are encoded). */
export function runIntentUri(source: string, runId?: string): string {
  const params = new URLSearchParams({ source });
  if (runId) params.set("run_id", runId);
  return `ato://run?${params.toString()}`;
}

/**
 * Emit an intent to the desktop by navigating to its `ato://` URL. The desktop
 * blocks the navigation and handles the intent natively; nothing loads in the
 * WebView. No-op (with a warning) outside the desktop.
 */
export function emitIntent(uri: string): void {
  if (!isDesktop()) {
    console.warn("[ato-desktop] emitIntent called outside Ato Desktop; ignored:", uri);
    return;
  }
  window.location.assign(uri);
}

/** Register this Desktop device as a personal Connected Runner (device-flow). */
export function runnerRegister(): void {
  emitIntent(RUNNER_REGISTER_URI);
}

/** Start the local Connected Runner agent. */
export function runnerStart(): void {
  emitIntent(RUNNER_START_URI);
}

/** Stop the local Connected Runner agent. */
export function runnerStop(): void {
  emitIntent(RUNNER_STOP_URI);
}

/** Ask the desktop to acknowledge a run request. Actual local run execution is a follow-up. */
export function runOnDesktop(source: string, runId?: string): void {
  emitIntent(runIntentUri(source, runId));
}

/**
 * Build an `ato://open?handle=…` intent URI. The desktop unwraps the target
 * and routes it like any NavigateToUrl input: an https session URL opens an
 * independent app window (its own icon in the Shell Icon Bar), a capsule://
 * ref goes through the native launch flow.
 */
export function openAppUri(target: string): string {
  const params = new URLSearchParams({ handle: target });
  return `ato://open?${params.toString()}`;
}

/**
 * Open a ready app in an independent Ato Desktop window. Inside the desktop
 * the embedded session console is disabled by design — apps never render as
 * iframes inside the Home WebView.
 */
export function openAppInDesktop(target: string): void {
  emitIntent(openAppUri(target));
}

/** How long the PWA waits for the desktop's launch ack before falling back to
 *  browser polling. Short by design — a desktop that can accept acks fast. */
export const DESKTOP_LAUNCH_ACK_TIMEOUT_MS = 3000;

/**
 * Hand a launch to the desktop over the injected IPC bridge (plan §4) and wait
 * for its ack, bounded by a short timeout. This NEVER throws and NEVER hangs:
 * outside the desktop, on an older desktop without `launch`, on a rejected /
 * throwing handler, or on timeout it resolves `{ accepted: false, reason }` —
 * the caller then continues normal browser polling so the launch is never
 * orphaned. Only the launch_id (+ capsule ref) crosses the boundary.
 */
export async function requestDesktopLaunch(
  launchId: string,
  capsuleRef?: string,
  timeoutMs: number = DESKTOP_LAUNCH_ACK_TIMEOUT_MS,
): Promise<DesktopLaunchAck> {
  const info = desktopInfo();
  const launch = info ? window.__ATO_DESKTOP__?.launch : undefined;
  if (!info || typeof launch !== "function") {
    return { accepted: false, reason: "unavailable" };
  }
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const timeout = new Promise<DesktopLaunchAck>((resolve) => {
      timer = setTimeout(
        () => resolve({ accepted: false, reason: "timeout" }),
        timeoutMs,
      );
    });
    const ack = await Promise.race([
      // Invoke on the injected object (an ipc_handler shim may rely on `this`).
      Promise.resolve(
        launch.call(window.__ATO_DESKTOP__, {
          launch_id: launchId,
          capsule_ref: capsuleRef,
        }),
      ),
      timeout,
    ]);
    return ack && ack.accepted === true
      ? { accepted: true }
      : { accepted: false, reason: ack?.reason ?? "rejected" };
  } catch (err) {
    return {
      accepted: false,
      reason: err instanceof Error ? err.message : "error",
    };
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}
